import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, X, ChevronDown, ChevronRight, Target, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  on_track:  { label: "On track",  colour: "text-[#00F5D4] bg-[#00F5D4]/10" },
  at_risk:   { label: "At risk",   colour: "text-[#F59E0B] bg-[#F59E0B]/10" },
  off_track: { label: "Off track", colour: "text-[#FF007A] bg-[#FF007A]/10" },
  done:      { label: "Done",      colour: "text-[#6B7280] bg-[#1F2937]" },
};

const QUARTERS = ["Q1 2026", "Q2 2026", "Q3 2026", "Q4 2026", "Q1 2027", "Q2 2027"];

const ProgressRing = ({ pct, size = 48, stroke = 4 }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const colour = pct >= 70 ? "#00F5D4" : pct >= 40 ? "#F59E0B" : "#FF007A";
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1F2937" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={colour} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.5s ease" }}
      />
    </svg>
  );
};

const KRBar = ({ kr, goalId, onUpdate }) => {
  const [val, setVal] = useState(String(kr.current_value));
  const [editing, setEditing] = useState(false);
  const pct = kr.target_value > 0 ? Math.min((kr.current_value / kr.target_value) * 100, 100) : 0;
  const colour = pct >= 70 ? "#00F5D4" : pct >= 40 ? "#F59E0B" : "#FF007A";

  const save = async () => {
    const n = parseFloat(val);
    if (isNaN(n)) return setEditing(false);
    try {
      await api.patch(`/goals/${goalId}/key-results/${kr.kr_id}`, { current_value: n });
      onUpdate();
    } catch { toast.error("Update failed"); }
    setEditing(false);
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#9CA3AF]">{kr.title}</span>
        <div className="flex items-center gap-1">
          {editing ? (
            <input
              autoFocus
              type="number"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              onBlur={save}
              onKeyDown={(e) => e.key === "Enter" && save()}
              className="w-16 h-5 text-xs text-right bg-[#030712] border border-[#374151] text-white px-1 rounded-none"
            />
          ) : (
            <button
              onClick={() => { setVal(String(kr.current_value)); setEditing(true); }}
              className="text-xs font-mono text-white hover:text-[#00F5D4] transition-colors"
              title="Click to update"
            >
              {kr.current_value}
            </button>
          )}
          <span className="text-xs text-[#4B5563]">/ {kr.target_value} {kr.unit}</span>
        </div>
      </div>
      <div className="w-full h-1 bg-[#1F2937] rounded-full overflow-hidden">
        <div style={{ width: `${pct}%`, background: colour }} className="h-full rounded-full transition-all" />
      </div>
    </div>
  );
};

const GoalCard = ({ goal, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = STATUS_META[goal.status] || STATUS_META.on_track;

  return (
    <div className="border border-[#1F2937] bg-[#0D1117]" data-testid={`goal-card-${goal.goal_id}`}>
      <div
        className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-[#111827] transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="relative flex-shrink-0">
          <ProgressRing pct={goal.progress} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[10px] font-mono text-white rotate-90" style={{ transform: "none" }}>
              {Math.round(goal.progress)}%
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <h3 className="font-semibold text-white text-sm leading-tight">{goal.title}</h3>
            <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 flex-shrink-0 ${meta.colour}`}>{meta.label}</span>
          </div>
          {goal.quarter && <div className="text-[11px] text-[#6B7280] mt-0.5">{goal.quarter}{goal.owner ? ` · ${goal.owner}` : ""}</div>}
          {goal.key_results?.length > 0 && (
            <div className="text-[11px] text-[#4B5563] mt-1">{goal.key_results.length} key result{goal.key_results.length !== 1 ? "s" : ""}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Select
            value={goal.status}
            onValueChange={async (v) => {
              try { await api.patch(`/goals/${goal.goal_id}`, { status: v }); onUpdate(); }
              catch { toast.error("Update failed"); }
            }}
          >
            <SelectTrigger
              className="rounded-none h-7 w-32 text-xs border-[#374151] bg-[#030712] text-white"
              onClick={(e) => e.stopPropagation()}
              data-testid={`goal-status-${goal.goal_id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(goal.goal_id); }}
            className="text-[#374151] hover:text-[#FF007A] transition-colors"
            data-testid={`delete-goal-${goal.goal_id}`}
          >
            <X className="w-4 h-4" />
          </button>
          {expanded ? <ChevronDown className="w-4 h-4 text-[#6B7280]" /> : <ChevronRight className="w-4 h-4 text-[#6B7280]" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#1F2937] px-5 py-4 space-y-3">
          {goal.description && <p className="text-sm text-[#9CA3AF]">{goal.description}</p>}
          {goal.key_results?.length > 0 && (
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">Key results</div>
              {goal.key_results.map((kr) => (
                <KRBar key={kr.kr_id} kr={kr} goalId={goal.goal_id} onUpdate={onUpdate} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const blankKR = () => ({ title: "", target_value: "100", current_value: "0", unit: "%" });

const Goals = () => {
  const [goals, setGoals] = useState([]);
  const [filterQ, setFilterQ] = useState("all");
  const [filterS, setFilterS] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", owner: "", quarter: "", due_date: "", key_results: [blankKR()] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterQ && filterQ !== "all") params.append("quarter", filterQ);
      if (filterS && filterS !== "all") params.append("status", filterS);
      const { data } = await api.get(`/goals?${params}`);
      setGoals(Array.isArray(data) ? data : []);
    } catch { toast.error("Failed to load goals"); }
  }, [filterQ, filterS]);

  useEffect(() => { load(); }, [load]);

  const addKR = () => setForm((f) => ({ ...f, key_results: [...f.key_results, blankKR()] }));
  const removeKR = (i) => setForm((f) => ({ ...f, key_results: f.key_results.filter((_, j) => j !== i) }));
  const setKR = (i, field, val) => setForm((f) => {
    const krs = [...f.key_results];
    krs[i] = { ...krs[i], [field]: val };
    return { ...f, key_results: krs };
  });

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      await api.post("/goals", {
        ...form,
        key_results: form.key_results
          .filter((kr) => kr.title.trim())
          .map((kr) => ({ ...kr, target_value: Number(kr.target_value) || 100, current_value: Number(kr.current_value) || 0 })),
      });
      toast.success("Goal created");
      setOpen(false);
      setForm({ title: "", description: "", owner: "", quarter: "", due_date: "", key_results: [blankKR()] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const deleteGoal = async (id) => {
    try { await api.delete(`/goals/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const onTrack = goals.filter((g) => g.status === "on_track").length;
  const atRisk = goals.filter((g) => g.status === "at_risk").length;
  const offTrack = goals.filter((g) => g.status === "off_track").length;
  const done = goals.filter((g) => g.status === "done").length;

  return (
    <div>
      <PageHeader
        eyebrow="Strategy"
        title="Goals & OKRs"
        description="Org-level objectives and key results — track progress across quarters."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="new-goal-btn">
                <Plus className="w-4 h-4 mr-1" /> New goal
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="tracking-tight">New goal</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Objective</Label>
                  <Input
                    className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                    placeholder="e.g. Grow ARR to $5M"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="goal-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Quarter</Label>
                    <Select value={form.quarter} onValueChange={(v) => setForm({ ...form, quarter: v })}>
                      <SelectTrigger className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white">
                        <SelectValue placeholder="— quarter —" />
                      </SelectTrigger>
                      <SelectContent>
                        {QUARTERS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Owner</Label>
                    <Input
                      className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                      placeholder="Name or team"
                      value={form.owner}
                      onChange={(e) => setForm({ ...form, owner: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Description</Label>
                  <Textarea
                    className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white"
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>

                {/* Key results */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Key results</Label>
                    <button onClick={addKR} className="text-[11px] text-[#00F5D4] hover:text-white flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Add KR
                    </button>
                  </div>
                  <div className="space-y-3">
                    {form.key_results.map((kr, i) => (
                      <div key={i} className="border border-[#1F2937] p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <Input
                            className="rounded-none flex-1 bg-[#030712] border-[#374151] text-white text-sm h-8"
                            placeholder="Key result title"
                            value={kr.title}
                            onChange={(e) => setKR(i, "title", e.target.value)}
                          />
                          <button onClick={() => removeKR(i)} className="text-[#4B5563] hover:text-[#FF007A]">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <div className="text-[9px] text-[#6B7280] mb-1">Current</div>
                            <Input className="rounded-none h-7 text-xs bg-[#030712] border-[#374151] text-white" type="number" value={kr.current_value} onChange={(e) => setKR(i, "current_value", e.target.value)} />
                          </div>
                          <div>
                            <div className="text-[9px] text-[#6B7280] mb-1">Target</div>
                            <Input className="rounded-none h-7 text-xs bg-[#030712] border-[#374151] text-white" type="number" value={kr.target_value} onChange={(e) => setKR(i, "target_value", e.target.value)} />
                          </div>
                          <div>
                            <div className="text-[9px] text-[#6B7280] mb-1">Unit</div>
                            <Input className="rounded-none h-7 text-xs bg-[#030712] border-[#374151] text-white" value={kr.unit} onChange={(e) => setKR(i, "unit", e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={busy} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="confirm-create-goal">
                  {busy ? "Saving…" : "Create goal"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-6 space-y-6">
        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#1F2937] border border-[#1F2937]">
          {[
            { label: "On track", val: onTrack, colour: "text-[#00F5D4]" },
            { label: "At risk", val: atRisk, colour: "text-[#F59E0B]" },
            { label: "Off track", val: offTrack, colour: "text-[#FF007A]" },
            { label: "Done", val: done, colour: "text-[#6B7280]" },
          ].map((s) => (
            <div key={s.label} className="bg-[#0D1117] px-5 py-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{s.label}</div>
              <div className={`text-3xl font-mono font-bold mt-1 ${s.colour}`}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <Select value={filterQ} onValueChange={setFilterQ}>
            <SelectTrigger className="rounded-none h-8 w-36 text-xs border-[#374151] bg-[#0D1117] text-white">
              <SelectValue placeholder="All quarters" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All quarters</SelectItem>
              {QUARTERS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterS} onValueChange={setFilterS}>
            <SelectTrigger className="rounded-none h-8 w-36 text-xs border-[#374151] bg-[#0D1117] text-white">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Goals list */}
        {goals.length === 0 ? (
          <div className="border border-[#1F2937] py-16 text-center" data-testid="goals-empty">
            <Target className="w-8 h-8 mx-auto text-[#374151] mb-3" />
            <div className="text-sm text-[#6B7280]">No goals yet. Create your first objective.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {goals.map((g) => (
              <GoalCard key={g.goal_id} goal={g} onUpdate={load} onDelete={deleteGoal} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Goals;
